import { type TrackStatus } from "@engine/round/aggregate";
import { accessiblePhases } from "./roundStatusUtils";

export type NavItemState = "active" | "dimmed" | "disabled";

export type NavItemVisibility = {
  state: NavItemState;
  tooltip?: string;
};

export type NavVisibility = {
  setup: {
    onboarding: NavItemVisibility;
    recusals: NavItemVisibility;
    investigations: NavItemVisibility;
  };
  proposals: NavItemVisibility;
  mvfs: NavItemVisibility;
  disagreements: NavItemVisibility;
  analysis: NavItemVisibility;
};

const PHASE_ORDER: TrackStatus[] = [
  "1.onboarding",
  "2.initial-evaluations",
  "3.full-discussion",
  "4.evals-freeze",
  "5.complete",
];

export function pickEffectivePhase(
  phases: TrackStatus[] | null,
): TrackStatus | null {
  if (!phases || phases.length === 0) {
    return null;
  }

  const first = phases[0];

  if (!first) {
    return null;
  }

  let best: TrackStatus = first;

  for (const p of phases) {
    if (PHASE_ORDER.indexOf(p) > PHASE_ORDER.indexOf(best)) {
      best = p;
    }
  }

  return best;
}

export const PHASE_LABEL: Record<TrackStatus, string> = {
  "1.onboarding": "Onboarding",
  "2.initial-evaluations": "Initial Evals",
  "3.full-discussion": "Full Discussion",
  "4.evals-freeze": "Evals Freeze",
  "5.complete": "Complete",
};

const OPENS_IN_INITIAL = "Opens in Initial Evals";
const OPENS_IN_FULL_DISCUSSION = "Opens in Full Discussion";

export function getNavVisibility(
  phases: TrackStatus | TrackStatus[] | null,
  opts: { isAdmin?: boolean; isFunder?: boolean } = {},
): NavVisibility {
  const visiblePhases = Array.isArray(phases) ? phases : phases ? [phases] : [];
  const phase = pickEffectivePhase(visiblePhases);
  const base = getBaseNavVisibility(phase);

  // Match the Onboarding page's phase gate; dimmed items remain clickable.
  if (
    !opts.isAdmin &&
    phases !== null &&
    !visiblePhases.some((visiblePhase) =>
      accessiblePhases("Onboarding", Boolean(opts.isFunder)).includes(
        visiblePhase,
      ),
    )
  ) {
    base.setup.onboarding = {
      state: "disabled",
      tooltip:
        visiblePhases.length === 0
          ? "Onboarding is unavailable because no tracks are visible"
          : "Onboarding is closed after Full Discussion",
    };
  }

  if (opts.isAdmin) {
    return {
      ...base,
      proposals: { state: "active" },
      mvfs: { state: "active" },
      disagreements: { state: "active" },
      analysis: { state: "active" },
    };
  }

  if (opts.isFunder && phase === "2.initial-evaluations") {
    return {
      ...base,
      disagreements: { state: "active" },
      analysis: { state: "active" },
    };
  }

  return base;
}

function getBaseNavVisibility(phase: TrackStatus | null): NavVisibility {
  switch (phase) {
    case "1.onboarding":
      return {
        setup: {
          onboarding: { state: "active" },
          recusals: { state: "disabled", tooltip: OPENS_IN_INITIAL },
          investigations: { state: "disabled", tooltip: OPENS_IN_INITIAL },
        },
        proposals: { state: "active" },
        mvfs: { state: "disabled", tooltip: OPENS_IN_INITIAL },
        disagreements: {
          state: "disabled",
          tooltip: OPENS_IN_FULL_DISCUSSION,
        },
        analysis: { state: "disabled", tooltip: OPENS_IN_FULL_DISCUSSION },
      };
    case "2.initial-evaluations":
      return {
        setup: {
          onboarding: { state: "active" },
          recusals: { state: "active" },
          investigations: { state: "active" },
        },
        proposals: { state: "active" },
        mvfs: { state: "active" },
        disagreements: {
          state: "disabled",
          tooltip: OPENS_IN_FULL_DISCUSSION,
        },
        analysis: { state: "disabled", tooltip: OPENS_IN_FULL_DISCUSSION },
      };
    case "3.full-discussion":
      return {
        setup: {
          onboarding: { state: "active" },
          recusals: { state: "active" },
          investigations: { state: "active" },
        },
        proposals: { state: "active" },
        mvfs: { state: "active" },
        disagreements: { state: "active" },
        analysis: { state: "active" },
      };
    case "4.evals-freeze":
      return {
        setup: {
          onboarding: { state: "dimmed" },
          recusals: { state: "active" },
          investigations: { state: "active" },
        },
        proposals: { state: "active" },
        mvfs: {
          state: "dimmed",
          tooltip: "Standard MVFs are read-only during evals freeze",
        },
        disagreements: { state: "active" },
        analysis: { state: "active" },
      };
    case "5.complete":
      return {
        setup: {
          onboarding: { state: "dimmed" },
          recusals: { state: "active" },
          investigations: { state: "active" },
        },
        proposals: { state: "active" },
        mvfs: { state: "dimmed", tooltip: "Round complete" },
        disagreements: { state: "active" },
        analysis: { state: "active" },
      };
    default:
      return {
        setup: {
          onboarding: { state: "active" },
          recusals: { state: "active" },
          investigations: { state: "active" },
        },
        proposals: { state: "active" },
        mvfs: { state: "active" },
        disagreements: { state: "active" },
        analysis: { state: "active" },
      };
  }
}
